import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Table from "../../../core/common/dataTable/index";
import { getAdminActivityLogs } from "../../../core/data/redux/auditLogsSlice";
import { all_routes } from "../../router/all_routes";
import { formatDateTime } from "../../../utils/dateUtils";

const AdminActivityLogs = () => {
  const routes = all_routes;
  const dispatch = useDispatch();
  const auditLogState = useSelector((state: any) => state.auditLogState);

  const [dataList, setDataList] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchRecords = (pageConfig: any) => {
    dispatch(
      getAdminActivityLogs({
        page: pageConfig.current,
        limit: pageConfig.pageSize,
        orderColumn: "created_at",
        orderBy: "DESC",
      })
    );
  };

  const handleRefresh = () => {
    fetchRecords(pagination);
  };

  const handlePagination = (paginationEvent: any) => {
    const newPagination = {
      current: paginationEvent.current,
      pageSize: paginationEvent.pageSize,
      total: pagination.total,
    };
    setPagination(newPagination);
    fetchRecords(newPagination);
  };

  useEffect(() => {
    fetchRecords(pagination);
  }, []);

  useEffect(() => {
    if (
      auditLogState.loading === "fulfilled" &&
      auditLogState.action === "list"
    ) {
      const res = auditLogState.data;

      // Extract list and pagination depending on response nesting
      let list: any[] = [];
      let totalCount = 0;
      let limitVal = pagination.pageSize;

      if (res?.data?.data && Array.isArray(res.data.data)) {
        list = res.data.data;
        totalCount = res.data.total ?? list.length;
        limitVal = res.data.limit ?? limitVal;
      } else if (res?.data && Array.isArray(res.data)) {
        list = res.data;
        totalCount = res.total ?? list.length;
        limitVal = res.limit ?? limitVal;
      } else if (Array.isArray(res)) {
        list = res;
        totalCount = list.length;
      }

      setDataList(list);
      setPagination((prev) => ({
        ...prev,
        total: totalCount,
        pageSize: limitVal,
      }));
    } else if (auditLogState.loading === "rejected") {
      toast.error(auditLogState?.error || "Failed to fetch admin activity logs");
    }
  }, [auditLogState.loading, auditLogState.action]);

  const renderMetadata = (record: any) => {
    const metadata = record?.request_metadata || record?.payload || record?.details;
    const errorMsg = record?.error_message;

    if (!metadata && !errorMsg) return <span>-</span>;

    return (
      <div style={{ maxWidth: "340px" }}>
        {errorMsg && (
          <div className="alert alert-danger py-1 px-2 mb-1 small text-break">
            <strong>Error:</strong> {errorMsg}
          </div>
        )}
        {metadata && (
          <pre
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              padding: "6px 10px",
              borderRadius: "6px",
              maxHeight: "150px",
              overflowY: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontSize: "11px",
              color: "#212529",
              margin: 0,
            }}
          >
            {typeof metadata === "object"
              ? JSON.stringify(metadata, null, 2)
              : (() => {
                  try {
                    return JSON.stringify(JSON.parse(metadata), null, 2);
                  } catch {
                    return String(metadata);
                  }
                })()}
          </pre>
        )}
      </div>
    );
  };

  const getMethodBadgeClass = (method?: string) => {
    switch (String(method).toUpperCase()) {
      case "GET":
        return "bg-info-transparent text-info";
      case "POST":
        return "bg-success-transparent text-success";
      case "PUT":
      case "PATCH":
        return "bg-warning-transparent text-warning";
      case "DELETE":
        return "bg-danger-transparent text-danger";
      default:
        return "bg-secondary-transparent text-secondary";
    }
  };

  const getStatusBadgeClass = (code?: number) => {
    if (!code) return "bg-secondary-transparent text-secondary";
    if (code >= 200 && code < 300) return "bg-success-transparent text-success";
    if (code >= 400 && code < 500) return "bg-warning-transparent text-warning";
    return "bg-danger-transparent text-danger";
  };

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 70,
      render: (_: any, record: any, index: number) => (
        <div>
          {pagination.current
            ? index + pagination.current * pagination.pageSize - pagination.pageSize + 1
            : index + 1}
        </div>
      ),
    },
    {
      title: "User / Role",
      dataIndex: "username",
      render: (_: any, record: any) => {
        const username = record?.username || record?.name || record?.email || "-";
        const empId = record?.employee_id || record?.empId || record?.user_id;
        const role = record?.role;

        return (
          <div>
            <div className="fw-semibold">{username}</div>
            {empId && <div className="text-muted small">ID: {empId}</div>}
            {role && (
              <span className="badge bg-primary-transparent text-primary mt-1">
                {role}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Module & API Endpoint",
      dataIndex: "module",
      render: (_: any, record: any) => {
        const moduleName = record?.module || "-";
        const endpoint = record?.api_endpoint || record?.endpoint || "";
        const method = record?.http_method || record?.method || "POST";

        return (
          <div>
            <div className="fw-semibold">{moduleName}</div>
            <div className="d-flex align-items-center gap-1 mt-1">
              <span className={`badge ${getMethodBadgeClass(method)}`}>
                {method}
              </span>
              <span className="text-muted small text-break">{endpoint}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Action & Status",
      dataIndex: "action",
      render: (_: any, record: any) => {
        const actionName = record?.action || "-";
        const statusCode = record?.status_code;
        const duration = record?.duration_ms;

        return (
          <div>
            <div className="badge bg-secondary-transparent text-dark">{actionName}</div>
            <div className="d-flex align-items-center gap-1 mt-1">
              {statusCode && (
                <span className={`badge ${getStatusBadgeClass(statusCode)}`}>
                  {statusCode}
                </span>
              )}
              {duration !== undefined && duration !== null && (
                <span className="text-muted small">{duration} ms</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Request Details / Error",
      dataIndex: "request_metadata",
      width: 350,
      render: (_: any, record: any) => renderMetadata(record),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (text: string, record: any) => {
        const dateVal = text || record?.created_at || record?.request_time;
        return dateVal ? formatDateTime(dateVal) : "-";
      },
    },
  ];

  return (
    <>
      <style>
        {`
          .spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Admin Activity Logs</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Logs</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Admin Activity Logs
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <button
                  className="btn btn-primary d-flex align-items-center"
                  onClick={handleRefresh}
                  disabled={auditLogState.loading === "pending"}
                  title="Refresh Admin Activity Logs"
                >
                  <i
                    className={`ti ti-refresh me-2 ${
                      auditLogState.loading === "pending" ? "spin" : ""
                    }`}
                  />
                  {auditLogState.loading === "pending"
                    ? "Refreshing..."
                    : "Refresh"}
                </button>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-4 mt-3">Admin Activity Logs</h4>
            </div>
            <div className="card-body p-0 py-3">
              <Table
                columns={columns}
                dataSource={dataList}
                Selection={false}
                paginationConfig={pagination}
                handlePagination={handlePagination}
                loading={
                  auditLogState.loading === "pending" &&
                  auditLogState.action === "list"
                }
              >
                <div></div>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminActivityLogs;
